export type GatewayReadinessRequirement = {
  key: 'CURSOR_API_KEY' | 'CURSOR_CLI';
  satisfied: boolean;
  message?: string;
};

export type CursorCliStatusForReadiness = {
  available: boolean;
  message?: string;
};

export type ComputeGatewayReadinessInput = {
  state: string;
  configured: boolean;
  hasProjectPath: boolean;
  requirements?: GatewayReadinessRequirement[] | null;
  isChatRunning: boolean;
  hasPendingRun: boolean;
};

export function hasUnsatisfiedCliRequirement(
  requirements?: GatewayReadinessRequirement[] | null,
): boolean {
  return requirements?.some(
    requirement => requirement.key === 'CURSOR_CLI' && !requirement.satisfied,
  ) ?? false;
}

/**
 * Mirrors {@link AssistantStore.applyStatus} readiness: configured project with all
 * backend requirements satisfied and gateway lifecycle in a send-capable state.
 */
export function computeGatewayReadiness({
  state,
  configured,
  hasProjectPath,
  requirements,
  isChatRunning,
  hasPendingRun,
}: ComputeGatewayReadinessInput): boolean {
  if (!configured || !hasProjectPath) {
    return false;
  }
  if (hasUnsatisfiedCliRequirement(requirements)) {
    return false;
  }
  if (state === 'error') {
    return false;
  }
  return state === 'ready' || isChatRunning || hasPendingRun;
}

export type AssistantGatewayBackendId = 'cursor-cli-acp' | 'cursor-sdk';

/**
 * Overrides the `CURSOR_CLI` requirement with a freshly probed CLI status so metadata
 * sync does not apply a stale requirement from a concurrent `getStatus()` call.
 * Only applies when the active backend is Cursor CLI ACP.
 */
export function mergeCursorCliRequirement(
  requirements: GatewayReadinessRequirement[] | undefined,
  cliStatus: CursorCliStatusForReadiness | null | undefined,
  activeBackendId?: AssistantGatewayBackendId | null,
): GatewayReadinessRequirement[] | undefined {
  if (activeBackendId !== 'cursor-cli-acp') {
    return requirements;
  }
  if (!cliStatus) {
    return requirements;
  }

  const merged = requirements ? [...requirements] : [];
  const cliIndex = merged.findIndex(requirement => requirement.key === 'CURSOR_CLI');
  const cliRequirement: GatewayReadinessRequirement = {
    key: 'CURSOR_CLI',
    satisfied: cliStatus.available,
    message: cliStatus.available
      ? undefined
      : cliStatus.message ?? 'Cursor CLI ACP runtime is required.',
  };

  if (cliIndex >= 0) {
    merged[cliIndex] = cliRequirement;
  } else {
    merged.push(cliRequirement);
  }

  return merged;
}
